import { useMutation, useQueryClient } from '@tanstack/vue-query'
import { useI18n } from 'vue-i18n'
import { toast } from 'vue-sonner'
import { createOrganization } from '@/features/organizations/api/create-organization'
import { getMyOrganizations } from '@/features/organizations/api/get-my-organizations'
import { MY_ORGANIZATIONS_QUERY_KEY } from '@/features/organizations/queries/useMyOrganizations'
import type { ApiError } from '@/lib/api/errors'
import { useWorkspaceStore } from '@/stores/workspace'

export function useCreateOrganization() {
  const queryClient = useQueryClient()
  const workspace = useWorkspaceStore()
  const { t } = useI18n()

  return useMutation({
    mutationFn: createOrganization,
    onSuccess: async (organization) => {
      // Reload first so the new organization (and the permissions it brings) is in the store.
      workspace.setOrganizations(
        await queryClient.fetchQuery({
          queryKey: MY_ORGANIZATIONS_QUERY_KEY,
          queryFn: getMyOrganizations,
          staleTime: 0,
        }),
      )
      workspace.setCurrentOrganization(String(organization.id))
      toast.success(t('organizations.toast.created'))
    },
    onError: (error: unknown) => {
      const apiError = error as ApiError
      // Field errors (e.g. an email already in use) are shown next to the field by the form.
      if (apiError.fieldErrors && Object.keys(apiError.fieldErrors).length > 0) return

      toast.error(apiError.message || t('organizations.toast.createFailed'))
    },
  })
}
